import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useCRM } from '../context/CRMContext';
import { SEED_UNIVERSITIES, COUNTRIES, PROGRAM_CATEGORIES, DEGREE_TYPES, AVAILABLE_PROGRAMS } from '../data/universityData';
import { db } from '../firebase/config';
import {
  collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp,
} from 'firebase/firestore';

// ─── helpers ─────────────────────────────────────────────────────────────────
const fmt = (n, cur) => {
  if (!n && n !== 0) return '—';
  if (cur === 'INR') return '₹' + n.toLocaleString('en-IN');
  if (cur === 'USD') return '$' + n.toLocaleString();
  if (cur === 'GBP') return '£' + n.toLocaleString();
  if (cur === 'AUD') return 'A$' + n.toLocaleString();
  if (cur === 'CAD') return 'C$' + n.toLocaleString();
  if (cur === 'EUR') return '€' + n.toLocaleString();
  if (cur === 'NZD') return 'NZ$' + n.toLocaleString();
  if (cur === 'CHF') return 'CHF ' + n.toLocaleString();
  return n.toLocaleString();
};

const countryFlag = (code) => COUNTRIES.find(c => c.code === code)?.flag || '🌏';
const countryLabel = (code) => COUNTRIES.find(c => c.code === code)?.label || code;

// ─── Degree-level program rankings ─────────────────────────────────────────
// Maps the page's broad "Program Category" filter onto the 6 program keys
// that have real undergrad-vs-grad ranking data (rankings.ugCS/gradCS etc).
// Law/Medicine/Education have no US News undergraduate list (JD/MD/EdD are
// graduate-only credentials), so their ug* fields are always null — callers
// should simply not surface them while degreeLevel === 'ug'.
const PROGRAM_FIELD_MAP = {
  'Computer Science & IT': 'CS',
  'Engineering': 'Engineering',
  'Business & Management': 'Business',
  'Law': 'Law',
  'Health Sciences': 'Medicine',
  'Education': 'Education',
};
const getProgramRank = (u, programCategory, degreeLevel) => {
  const field = PROGRAM_FIELD_MAP[programCategory];
  if (!field) return null;
  const key = (degreeLevel === 'ug' ? 'ug' : 'grad') + field;
  return u.rankings?.[key] ?? null;
};

// ─── Shared filter logic ───────────────────────────────────────────────────────
// Single source of truth for "does university u match filter object f", used by
// BOTH the main Universities grid AND the ShortlistModal, so the two never drift
// out of sync again. f can contain any subset of these keys; absent/empty keys
// are treated as "no constraint".
const emptyFilters = {
  search: '', country: '', program: '', programCategory: '',
  degree: '', qsRankMax: '', usNewsRankMax: '', feeMax: '', intake: '',
};

const matchesFilters = (u, f = emptyFilters, liveSearch = '') => {
  // 1. Country — strict exact match on country code
  if (f.country && u.country !== f.country) return false;

  // 2. Specific program — search availablePrograms array only
  if (f.program) {
    const q = f.program.toLowerCase();
    if (!(u.availablePrograms || []).some(p => p.toLowerCase().includes(q))) return false;
  }

  // 3. Program category — broad category in programs[]
  if (f.programCategory && !(u.programs || []).some(p => p === f.programCategory)) return false;

  // 4. Degree type
  if (f.degree && !(u.degreeTypes || []).some(d => d === f.degree)) return false;

  // 5. QS rank max — only show universities WITH a rank, within the limit
  if (f.qsRankMax) {
    const max = Number(f.qsRankMax);
    if (!u.qsRank || u.qsRank > max) return false;
  }

  // 6. US News rank max — only show universities WITH a rank, within the limit
  if (f.usNewsRankMax) {
    const max = Number(f.usNewsRankMax);
    if (!u.usNewsRank || u.usNewsRank > max) return false;
  }

  // 7. Max tuition — skip universities with no tuition data
  if (f.feeMax && u.tuitionMin && u.tuitionMin > Number(f.feeMax)) return false;

  // 8. Intake month
  if (f.intake && !(u.intakes || []).includes(f.intake)) return false;

  // 9. Live text search — name, city and country NAME (not raw country code, to
  //    avoid "us" matching AUSTRALIA / AUSTRIA)
  if (liveSearch) {
    const q = liveSearch.toLowerCase();
    const cLabel = countryLabel(u.country).toLowerCase();
    return (
      u.name.toLowerCase().includes(q) ||
      (u.city || '').toLowerCase().includes(q) ||
      cLabel.includes(q)
    );
  }

  return true;
};

// ─── Pill ─────────────────────────────────────────────────────────────────────
function Pill({ label, color = '#7c3aed' }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 20,
      background: color + '18', color, fontSize: 10, fontWeight: 600, margin: '2px 2px 2px 0',
    }}>{label}</span>
  );
}

// ─── FilterChip ───────────────────────────────────────────────────────────────
function FilterChip({ label, onRemove }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
      background: 'rgba(124,58,237,0.1)', color: '#7c3aed',
      border: '1px solid rgba(124,58,237,0.25)',
    }}>
      {label}
      <button onClick={onRemove} style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#7c3aed', fontSize: 13, lineHeight: 1, padding: 0, marginLeft: 2,
      }}>×</button>
    </span>
  );
}

// ─── University Card ──────────────────────────────────────────────────────────
function UniversityCard({ u, onSelect, selected, onEdit, onDelete, isAdmin, inShortlist, onToggleShortlist, programCategory, degreeLevel }) {
  const cObj = COUNTRIES.find(c => c.code === u.country);
  const flagEmoji = cObj?.flag || '🌏';
  const cName = cObj ? cObj.label.replace(/^[^ ]+ /, '') : u.country;
  const programRank = getProgramRank(u, programCategory, degreeLevel);

  return (
    <div onClick={() => onSelect(u)} style={{
      background: 'var(--bg-card)', border: selected ? '2px solid #7c3aed' : inShortlist ? '2px solid #10b981' : '1px solid var(--border)',
      borderRadius: 14, padding: 18, cursor: 'pointer', transition: 'all 0.15s',
      boxShadow: selected ? '0 0 0 3px rgba(124,58,237,0.15)' : 'var(--shadow-card)',
      position: 'relative',
    }}>
      {onToggleShortlist && (
        <label
          onClick={e => e.stopPropagation()}
          title="Add to shortlist selection"
          style={{
            position: 'absolute', top: 10, left: 10, display: 'flex', alignItems: 'center',
            gap: 4, cursor: 'pointer', zIndex: 1,
          }}
        >
          <input
            type="checkbox"
            checked={!!inShortlist}
            onChange={() => onToggleShortlist(u)}
            style={{ width: 15, height: 15, cursor: 'pointer', accentColor: '#10b981' }}
          />
        </label>
      )}
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, paddingLeft: onToggleShortlist ? 18 : 0 }}>
        <div style={{ flexShrink: 0, textAlign: 'center' }}>
          <div style={{ fontSize: 26, lineHeight: 1 }}>{flagEmoji}</div>
          <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 2, whiteSpace: 'nowrap' }}>{cName}</div>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3, paddingRight: isAdmin ? 52 : 0 }}>{u.name}</div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 }}>{u.city}</div>
        </div>
        {selected && <span style={{ color: '#7c3aed', fontSize: 16, flexShrink: 0 }}>✓</span>}
      </div>

      {/* Rankings */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
        {u.qsRank && (
          <div style={{ flex: 1, background: 'rgba(124,58,237,0.07)', borderRadius: 8, padding: '6px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#7c3aed' }}>#{u.qsRank}</div>
            <div style={{ fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}>QS World</div>
          </div>
        )}
        {u.usNewsRank && (
          <div style={{ flex: 1, background: 'rgba(14,165,233,0.07)', borderRadius: 8, padding: '6px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#0ea5e9' }}>#{u.usNewsRank}</div>
            <div style={{ fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}>US News</div>
          </div>
        )}
        {!programRank && u.acceptanceRate && (
          <div style={{ flex: 1, background: 'rgba(16,185,129,0.07)', borderRadius: 8, padding: '6px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#10b981' }}>{u.acceptanceRate}%</div>
            <div style={{ fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}>Accept.</div>
          </div>
        )}
        {programRank && (
          <div style={{ flex: 1, background: 'rgba(245,158,11,0.1)', borderRadius: 8, padding: '6px 10px', textAlign: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: '#d97706' }}>#{programRank}</div>
            <div style={{ fontSize: 9, color: 'var(--text-secondary)', fontWeight: 600 }}>{PROGRAM_FIELD_MAP[programCategory]} ({degreeLevel === 'ug' ? 'UG' : 'Grad'})</div>
          </div>
        )}
      </div>

      {/* Fees */}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>
        <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>
          {fmt(u.tuitionMin, u.currency)} – {fmt(u.tuitionMax, u.currency)}
        </span>
        <span style={{ fontSize: 10 }}> / year</span>
      </div>

      {/* Programs */}
      <div style={{ marginBottom: 8 }}>
        {(u.programs || []).slice(0, 3).map(p => <Pill key={p} label={p} />)}
        {(u.programs || []).length > 3 && <Pill label={`+${u.programs.length - 3} more`} color="#64748b" />}
      </div>

      {/* Intakes */}
      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
        🗓 Intakes: {(u.intakes || []).join(', ')}
        {u.ielts && <span style={{ marginLeft: 10 }}>📝 IELTS {u.ielts}+</span>}
      </div>

      {isAdmin && (
        <div style={{ position: 'absolute', top: 10, right: 10, display: 'flex', gap: 4 }}
          onClick={e => e.stopPropagation()}>
          <button onClick={() => onEdit(u)} style={{
            background: 'rgba(124,58,237,0.1)', border: 'none', borderRadius: 6,
            padding: '3px 7px', cursor: 'pointer', fontSize: 11, color: '#7c3aed'
          }}>✏️</button>
          {u._custom && (
            <button onClick={() => onDelete(u)} style={{
              background: 'rgba(239,68,68,0.1)', border: 'none', borderRadius: 6,
              padding: '3px 7px', cursor: 'pointer', fontSize: 11, color: '#ef4444'
            }}>🗑</button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Add/Edit Modal ───────────────────────────────────────────────────────────
function UniversityModal({ uni, onClose, onSave }) {
  const blank = {
    name: '', country: 'USA', city: '', qsRank: '', usNewsRank: '',
    programs: [], degreeTypes: [], tuitionMin: '', tuitionMax: '', currency: 'USD',
    intakes: [], acceptanceRate: '', ielts: '', toefl: '', greRequired: false,
    website: '', established: '', campusType: 'Urban', about: '',
  };
  const [form, setForm] = useState(uni ? { ...blank, ...uni } : blank);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const toggleArr = (k, v) => setForm(f => ({
    ...f, [k]: f[k].includes(v) ? f[k].filter(x => x !== v) : [...f[k], v],
  }));

  const CURRENCIES = ['USD','GBP','AUD','CAD','EUR','INR','NZD','CHF','SGD'];
  const INTAKES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
  const CAMPUS_TYPES = ['Urban','Suburban','Campus','City','Rural'];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-primary)', borderRadius: 16, width: '100%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto', padding: 28 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)' }}>{uni ? 'Edit University' : 'Add University'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>University Name *</label>
            <input className="crm-input" value={form.name} onChange={e => set('name', e.target.value)} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Country</label>
            <select className="crm-select" value={form.country} onChange={e => set('country', e.target.value)} style={{ width: '100%', marginTop: 4 }}>
              {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>City</label>
            <input className="crm-input" value={form.city} onChange={e => set('city', e.target.value)} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>QS World Rank</label>
            <input type="number" className="crm-input" value={form.qsRank} onChange={e => set('qsRank', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>US News Rank</label>
            <input type="number" className="crm-input" value={form.usNewsRank} onChange={e => set('usNewsRank', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Tuition Min / year</label>
            <input type="number" className="crm-input" value={form.tuitionMin} onChange={e => set('tuitionMin', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Tuition Max / year</label>
            <input type="number" className="crm-input" value={form.tuitionMax} onChange={e => set('tuitionMax', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Currency</label>
            <select className="crm-select" value={form.currency} onChange={e => set('currency', e.target.value)} style={{ width: '100%', marginTop: 4 }}>
              {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Acceptance Rate (%)</label>
            <input type="number" className="crm-input" value={form.acceptanceRate} onChange={e => set('acceptanceRate', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>IELTS Min Score</label>
            <input type="number" className="crm-input" step="0.5" value={form.ielts} onChange={e => set('ielts', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>TOEFL Min Score</label>
            <input type="number" className="crm-input" value={form.toefl} onChange={e => set('toefl', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Website</label>
            <input className="crm-input" value={form.website} onChange={e => set('website', e.target.value)} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Established (Year)</label>
            <input type="number" className="crm-input" value={form.established} onChange={e => set('established', e.target.value ? Number(e.target.value) : '')} style={{ width: '100%', marginTop: 4 }} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>Campus Type</label>
            <select className="crm-select" value={form.campusType} onChange={e => set('campusType', e.target.value)} style={{ width: '100%', marginTop: 4 }}>
              {CAMPUS_TYPES.map(c => <option key={c}>{c}</option>)}
            </select>
          </div>

          {/* Intakes */}
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Intake Months</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {INTAKES.map(m => (
                <button key={m} type="button" onClick={() => toggleArr('intakes', m)} style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                  border: form.intakes.includes(m) ? '2px solid #7c3aed' : '1px solid var(--border)',
                  background: form.intakes.includes(m) ? 'rgba(124,58,237,0.1)' : 'transparent',
                  color: form.intakes.includes(m) ? '#7c3aed' : 'var(--text-secondary)', fontWeight: 600,
                }}>{m}</button>
              ))}
            </div>
          </div>

          {/* Programs */}
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Program Categories</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {PROGRAM_CATEGORIES.map(p => (
                <button key={p} type="button" onClick={() => toggleArr('programs', p)} style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                  border: form.programs.includes(p) ? '2px solid #7c3aed' : '1px solid var(--border)',
                  background: form.programs.includes(p) ? 'rgba(124,58,237,0.1)' : 'transparent',
                  color: form.programs.includes(p) ? '#7c3aed' : 'var(--text-secondary)', fontWeight: 600,
                }}>{p}</button>
              ))}
            </div>
          </div>

          {/* Degree Types */}
          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>Degree Types</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {DEGREE_TYPES.map(d => (
                <button key={d} type="button" onClick={() => toggleArr('degreeTypes', d)} style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 11, cursor: 'pointer',
                  border: form.degreeTypes.includes(d) ? '2px solid #0ea5e9' : '1px solid var(--border)',
                  background: form.degreeTypes.includes(d) ? 'rgba(14,165,233,0.1)' : 'transparent',
                  color: form.degreeTypes.includes(d) ? '#0ea5e9' : 'var(--text-secondary)', fontWeight: 600,
                }}>{d}</button>
              ))}
            </div>
          </div>

          <div style={{ gridColumn: '1/-1' }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' }}>About (1–2 sentences)</label>
            <textarea className="crm-input" value={form.about} onChange={e => set('about', e.target.value)}
              rows={2} style={{ width: '100%', marginTop: 4, resize: 'none' }} />
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
          <button onClick={onClose} style={{ padding: '9px 20px', borderRadius: 10, border: '1px solid var(--border)', background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>Cancel</button>
          <button onClick={() => onSave(form)} className="btn-primary" style={{ padding: '9px 24px' }}>
            {uni ? 'Save Changes' : 'Add University'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Shortlist PDF Generator ──────────────────────────────────────────────────
async function generateShortlistPDF({ student, universities, company, counsellor, notes, program, degreeType }) {
  // Dynamic import jsPDF + autotable (v5 API: autoTable(doc, options) not doc.autoTable)
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const W = 210, marginL = 18, marginR = 18;
  const contentW = W - marginL - marginR;

  // ── Brand Colors ──
  const purple = [124, 58, 237];
  const purpleLight = [237, 233, 254];
  const dark = [15, 23, 42];
  const gray = [71, 85, 105];
  const lightGray = [241, 245, 249];

  // ── COVER HEADER ──────────────────────────────────────────────────────────
  // Gradient header bar
  doc.setFillColor(...purple);
  doc.rect(0, 0, W, 52, 'F');

  // Decorative circle
  doc.setFillColor(255, 255, 255, 0.05);
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.3);
  doc.setFillColor(150, 100, 237);
  doc.circle(W - 20, 10, 28, 'F');
  doc.setFillColor(170, 130, 255);
  doc.circle(W - 15, 50, 18, 'F');

  // Company name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(255, 255, 255);
  doc.text(company?.name || 'Admissions Counseling', marginL, 20);

  // Tagline
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(220, 210, 255);
  doc.text(company?.tagline || 'Your Gateway to Global Education', marginL, 27);

  // Doc title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(255, 255, 255);
  doc.text('University Shortlist Report', marginL, 40);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(210, 200, 255);
  doc.text(`Prepared on ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`, marginL, 46);

  let y = 62;

  // ── STUDENT PROFILE BOX ───────────────────────────────────────────────────
  doc.setFillColor(...lightGray);
  doc.roundedRect(marginL, y, contentW, 38, 4, 4, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...dark);
  doc.text('Student Profile', marginL + 8, y + 8);

  // Left column
  const col1 = marginL + 8, col2 = marginL + contentW / 2;
  const studentData = [
    ['Name', student?.name || '—'],
    ['Email', student?.email || '—'],
    ['Phone', student?.phone || '—'],
  ];
  const studentData2 = [
    ['Program of Interest', program || student?.program || '—'],
    ['Degree Type', degreeType || student?.degreeType || '—'],
    ['Prepared by', counsellor || '—'],
  ];

  doc.setFontSize(8.5);
  studentData.forEach(([label, val], i) => {
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...gray);
    doc.text(label + ':', col1, y + 16 + i * 7);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark);
    doc.text(String(val), col1 + 32, y + 16 + i * 7);
  });
  studentData2.forEach(([label, val], i) => {
    doc.setFont('helvetica', 'bold'); doc.setTextColor(...gray);
    doc.text(label + ':', col2, y + 16 + i * 7);
    doc.setFont('helvetica', 'normal'); doc.setTextColor(...dark);
    doc.text(String(val).substring(0, 38), col2 + 40, y + 16 + i * 7);
  });

  y += 46;

  // ── INTRO LETTER ──────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...purple);
  doc.text('Dear ' + (student?.name?.split(' ')[0] || 'Student') + ',', marginL, y);
  y += 8;

  const intro = `We are delighted to present your personalised university shortlist, meticulously curated by our expert counseling team based on your academic profile, preferred program of study, and career aspirations. Each institution has been selected to maximise your chances of admission while aligning with your ambitions and budget.`;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...gray);
  const introLines = doc.splitTextToSize(intro, contentW);
  doc.text(introLines, marginL, y);
  y += introLines.length * 5 + 8;

  // ── SHORTLISTED UNIVERSITIES TABLE ────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...dark);
  doc.text('🎓  Shortlisted Universities', marginL, y);
  y += 6;

  const tableRows = universities.map((u, i) => [
    String(i + 1),
    u.name,
    u.country,
    u.qsRank ? '#' + u.qsRank : '—',
    u.usNewsRank ? '#' + u.usNewsRank : '—',
    u.tuitionMin && u.tuitionMax ? `${fmt(u.tuitionMin, u.currency)} – ${fmt(u.tuitionMax, u.currency)}` : '—',
    (u.intakes || []).join(', ') || '—',
    u.ielts ? u.ielts + '+' : '—',
  ]);

  autoTable(doc, {
    startY: y,
    head: [['#', 'University', 'Country', 'QS Rank', 'US News', 'Tuition / Year', 'Intakes', 'IELTS']],
    body: tableRows,
    theme: 'grid',
    headStyles: {
      fillColor: purple, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 8,
    },
    bodyStyles: { fontSize: 7.5, cellPadding: 3.5 },
    alternateRowStyles: { fillColor: [250, 248, 255] },
    columnStyles: {
      0: { cellWidth: 8, halign: 'center' },
      1: { cellWidth: 52 },
      2: { cellWidth: 20 },
      3: { cellWidth: 18, halign: 'center' },
      4: { cellWidth: 18, halign: 'center' },
      5: { cellWidth: 34 },
      6: { cellWidth: 20 },
      7: { cellWidth: 14, halign: 'center' },
    },
    margin: { left: marginL, right: marginR },
    didDrawPage: (data) => { y = data.cursor.y; },
  });

  y = (doc.lastAutoTable?.finalY || y) + 10;

  // ── UNIVERSITY DETAILS ────────────────────────────────────────────────────
  if (y > 240) { doc.addPage(); y = 18; }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor(...dark);
  doc.text('University Profiles', marginL, y);
  y += 8;

  universities.forEach((u, i) => {
    if (y > 255) { doc.addPage(); y = 18; }

    // Card background
    doc.setFillColor(...lightGray);
    const cardH = 44;
    doc.roundedRect(marginL, y, contentW, cardH, 3, 3, 'F');

    // Rank badge
    doc.setFillColor(...purple);
    doc.roundedRect(marginL, y, 10, cardH, 3, 3, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text(String(i + 1), marginL + 3, y + cardH / 2 + 2);

    // University name
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...dark);
    doc.text(u.name, marginL + 14, y + 9);

    // City + Country
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...gray);
    doc.text((u.city || '') + ' · ' + (u.country || ''), marginL + 14, y + 15);

    // About
    if (u.about) {
      const aboutLines = doc.splitTextToSize(u.about, contentW - 20);
      doc.text(aboutLines.slice(0, 2), marginL + 14, y + 22);
    }

    // Rankings + fees row
    const rankStr = [
      u.qsRank ? `QS: #${u.qsRank}` : null,
      u.usNewsRank ? `US News: #${u.usNewsRank}` : null,
      `Fees: ${fmt(u.tuitionMin, u.currency)}–${fmt(u.tuitionMax, u.currency)}/yr`,
      u.acceptanceRate ? `Acceptance: ${u.acceptanceRate}%` : null,
    ].filter(Boolean).join('   |   ');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    doc.setTextColor(...purple);
    doc.text(rankStr, marginL + 14, y + 38);

    y += cardH + 6;
  });

  // ── COUNSELLOR NOTES ──────────────────────────────────────────────────────
  if (notes) {
    if (y > 240) { doc.addPage(); y = 18; }
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(...dark);
    doc.text('Counsellor Notes', marginL, y);
    y += 7;
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(9);
    doc.setTextColor(...gray);
    const noteLines = doc.splitTextToSize(notes, contentW);
    doc.text(noteLines, marginL, y);
    y += noteLines.length * 5 + 6;
  }

  // ── CLOSING ───────────────────────────────────────────────────────────────
  if (y > 240) { doc.addPage(); y = 18; }
  y += 6;
  const closing = `We look forward to guiding you through every step of your application journey. Our team is committed to helping you secure admission to the best institution that aligns with your goals. Please do not hesitate to reach out with any questions.`;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(...gray);
  doc.text(doc.splitTextToSize(closing, contentW), marginL, y);
  y += 22;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...dark);
  doc.text('Warm regards,', marginL, y);
  y += 5;
  doc.text(counsellor || company?.name || 'The Admissions Team', marginL, y);
  if (company?.name) {
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...gray);
    doc.text(company.name, marginL, y);
  }

  // ── FOOTER on all pages ───────────────────────────────────────────────────
  const pageCount = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFillColor(...purple);
    doc.rect(0, 285, W, 12, 'F');
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(220, 210, 255);
    doc.text(company?.name || '', marginL, 292);
    doc.text('Confidential · For Student Use Only', W / 2, 292, { align: 'center' });
    doc.text(`Page ${p} of ${pageCount}`, W - marginR, 292, { align: 'right' });
  }

  return doc;
}

// ─── PDF Preview Modal ────────────────────────────────────────────────────────
function PDFPreviewModal({ pdfUrl, pdfBlob, fileName, lead, onClose, onEdit }) {
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = pdfUrl;
    a.download = fileName;
    a.click();
  };

  const handleWhatsApp = () => {
    const msg = encodeURIComponent(
      `Hi ${lead?.name || 'there'}! 🎓 Please find your personalised university shortlist attached. Feel free to reach out if you have any questions.`
    );
    const phone = (lead?.phone || '').replace(/\D/g, '');
    if (phone) {
      window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
    } else {
      window.open(`https://wa.me/?text=${msg}`, '_blank');
    }
  };

  const handleEmail = async () => {
    // Open mailto with subject/body; browser handles attachment limitation
    const subject = encodeURIComponent(`Your University Shortlist — ${lead?.name || ''}`);
    const body = encodeURIComponent(
      `Dear ${lead?.name || 'Student'},\n\nPlease find your personalised university shortlist attached to this email.\n\nFeel free to reach out if you have any questions.\n\nBest regards`
    );
    const email = lead?.email || '';
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
    // Also trigger download so they can attach manually
    handleDownload();
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 99999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 960, height: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 24px 80px rgba(0,0,0,0.4)' }}>
        {/* Header */}
        <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#fff', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#0f172a' }}>📄 Shortlist PDF — {lead?.name}</div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 1 }}>{fileName}</div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* WhatsApp */}
            <button onClick={handleWhatsApp} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: '#25d366', color: '#fff', fontWeight: 700, fontSize: 12,
            }}>
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </button>
            {/* Email */}
            <button onClick={handleEmail} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', borderRadius: 9, border: 'none', cursor: 'pointer',
              background: emailSent ? '#10b981' : '#7c3aed', color: '#fff', fontWeight: 700, fontSize: 12,
            }}>
              📧 {emailSent ? 'Email opened!' : 'Email'}
            </button>
            {/* Download */}
            <button onClick={handleDownload} style={{
              display: 'flex', alignItems: 'center', gap: 6,
              padding: '9px 16px', borderRadius: 9, border: '1px solid #e2e8f0', cursor: 'pointer',
              background: '#f8fafc', color: '#0f172a', fontWeight: 700, fontSize: 12,
            }}>
              ⬇ Download
            </button>
            {/* Edit (go back) */}
            <button onClick={onEdit} style={{
              padding: '9px 16px', borderRadius: 9, border: '1px solid #e2e8f0', cursor: 'pointer',
              background: '#f8fafc', color: '#64748b', fontWeight: 700, fontSize: 12,
            }}>
              ✏️ Edit
            </button>
            {/* Close */}
            <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
          </div>
        </div>
        {/* PDF Viewer */}
        <div style={{ flex: 1, overflow: 'hidden', background: '#525659' }}>
          <iframe
            src={pdfUrl}
            style={{ width: '100%', height: '100%', border: 'none' }}
            title="Shortlist PDF"
          />
        </div>
      </div>
    </div>
  );
}

// ─── Shortlist Modal (per lead) ───────────────────────────────────────────────
function ShortlistModal({
  lead, allUniversities, company, currentUser, onClose,
  initialSelected = [], initialFilters = {}, initialSearch = '',
}) {
  const { addShortlist } = useCRM();
  // Seeded from whatever the counselor already ticked/filtered on the main
  // Universities page — filtering and shortlisting are one continuous flow,
  // not two disconnected screens.
  const [selected, setSelected] = useState(initialSelected);
  const [program, setProgram] = useState(initialFilters.program || lead?.program || '');
  const [degreeType, setDegreeType] = useState('');
  const [notes, setNotes] = useState('');
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState(initialSearch || '');
  const [filterCountry, setFilterCountry] = useState(initialFilters.country || '');
  const [sortBy, setSortBy] = useState('qsRank');
  const [pdfPreview, setPdfPreview] = useState(null); // { url, blob, fileName }

  const filtered = useMemo(() => {
    // Country + max-tuition + degree + rank constraints carried over from the
    // main page's filter object use the SAME matchesFilters() the main grid
    // uses, so a university that passed the page filters never disappears here.
    return allUniversities
      .filter(u => matchesFilters(u, { ...initialFilters, country: filterCountry, program: '' }, ''))
      .filter(u => {
        // Program box here is intentionally broader than the main page's:
        // it matches either a specific program name OR a broad category,
        // since this is a single quick-search field rather than two filters.
        if (program) {
          const q = program.toLowerCase();
          const inAvail = (u.availablePrograms || []).some(p => p.toLowerCase().includes(q));
          const inCat = (u.programs || []).some(p => p.toLowerCase().includes(q));
          if (!inAvail && !inCat) return false;
        }
        if (search) {
          const q = search.toLowerCase();
          const cLabel = countryLabel(u.country).toLowerCase();
          return (
            u.name.toLowerCase().includes(q) ||
            (u.city || '').toLowerCase().includes(q) ||
            cLabel.includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => {
        if (sortBy === 'qsRank') return (a.qsRank || 9999) - (b.qsRank || 9999);
        if (sortBy === 'usNewsRank') return (a.usNewsRank || 9999) - (b.usNewsRank || 9999);
        if (sortBy === 'tuition') return (a.tuitionMin || 0) - (b.tuitionMin || 0);
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        return 0;
      });
  }, [allUniversities, filterCountry, program, search, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (u) => setSelected(s =>
    s.find(x => x.id === u.id) ? s.filter(x => x.id !== u.id) : [...s, u]
  );

  const handleGenerate = async () => {
    if (!selected.length) return;
    setGenerating(true);
    try {
      const pdf = await generateShortlistPDF({
        student: lead, universities: selected, company, program, degreeType, notes,
        counsellor: currentUser?.name,
      });
      const fileName = `Shortlist_${(lead?.name || 'Student').replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      setPdfPreview({ url, blob, fileName });
      // Save shortlist to Firestore
      if (addShortlist) {
        await addShortlist({
          leadId: lead?.id, leadName: lead?.name,
          universities: selected.map(u => ({ id: u.id, name: u.name, country: u.country, qsRank: u.qsRank })),
          program, degreeType, notes,
          createdBy: currentUser?.id, createdByName: currentUser?.name,
        });
      }
    } catch (e) {
      console.error(e);
      alert('PDF generation failed. Please try again.');
    }
    setGenerating(false);
  };

  // PDF Preview
  if (pdfPreview) {
    return (
      <PDFPreviewModal
        pdfUrl={pdfPreview.url}
        pdfBlob={pdfPreview.blob}
        fileName={pdfPreview.fileName}
        lead={lead}
        onClose={() => { URL.revokeObjectURL(pdfPreview.url); onClose(); }}
        onEdit={() => setPdfPreview(null)}
      />
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: 'var(--bg-primary)', borderRadius: 18, width: '100%', maxWidth: 880, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <div style={{ padding: '18px 24px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>🎓 Create Shortlist</h3>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>for {lead?.name}</p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
        </div>

        {(initialSelected.length > 0 || initialFilters.country || initialFilters.qsRankMax || initialFilters.usNewsRankMax || initialFilters.feeMax || initialFilters.degree || initialFilters.programCategory || initialFilters.intake) && (
          <div style={{ padding: '8px 24px', background: 'rgba(16,185,129,0.08)', fontSize: 11, color: '#10b981', fontWeight: 600, flexShrink: 0 }}>
            {initialSelected.length > 0 && `✓ Carried over ${initialSelected.length} pre-selected universities from your filtered view. `}
            Active page filters (country, degree, rank, fees, intake) are still applied here — adjust country/program below or clear them on the main page.
          </div>
        )}

        {/* Filters row */}
        <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', display: 'flex', gap: 10, flexWrap: 'wrap', flexShrink: 0 }}>
          <input className="crm-input" placeholder="Search universities…" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 2, minWidth: 160 }} />
          <select className="crm-select" value={filterCountry} onChange={e => setFilterCountry(e.target.value)} style={{ flex: 1, minWidth: 130 }}>
            <option value="">All Countries</option>
            {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.label.replace(/^[^ ]+ /, '')}</option>)}
          </select>
          <input className="crm-input" list="shortlist-prog-list" placeholder="Program (e.g. Computer Science)" value={program} onChange={e => setProgram(e.target.value)} style={{ flex: 2, minWidth: 150 }} />
          <datalist id="shortlist-prog-list">
            {(AVAILABLE_PROGRAMS || []).map(p => <option key={p} value={p} />)}
          </datalist>
          <select className="crm-select" value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ flex: 1, minWidth: 120 }}>
            <option value="qsRank">Sort: QS Rank</option>
            <option value="usNewsRank">Sort: US News</option>
            <option value="tuition">Sort: Tuition</option>
            <option value="name">Sort: Name</option>
          </select>
        </div>

        {/* Main area */}
        <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* University list */}
          <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 10 }}>
              {filtered.length} universities · click to select
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 12 }}>
              {filtered.map(u => (
                <div key={u.id} onClick={() => toggle(u)} style={{
                  border: selected.find(x => x.id === u.id) ? '2px solid #7c3aed' : '1px solid var(--border)',
                  borderRadius: 12, padding: 14, cursor: 'pointer',
                  background: selected.find(x => x.id === u.id) ? 'rgba(124,58,237,0.06)' : 'var(--bg-card)',
                }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <span style={{ fontSize: 20 }}>{countryFlag(u.country)}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3 }}>{u.name}</div>
                      <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{u.city}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                        {u.qsRank && <span style={{ fontSize: 10, fontWeight: 700, color: '#7c3aed' }}>QS #{u.qsRank}</span>}
                        {u.usNewsRank && <span style={{ fontSize: 10, fontWeight: 700, color: '#0ea5e9' }}>US #{u.usNewsRank}</span>}
                        <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{fmt(u.tuitionMin, u.currency)}/yr</span>
                      </div>
                    </div>
                    {selected.find(x => x.id === u.id) && (
                      <span style={{ color: '#7c3aed', fontSize: 16, flexShrink: 0 }}>✓</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right panel */}
          <div style={{ width: 260, borderLeft: '1px solid var(--border)', padding: 16, overflowY: 'auto', flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 12 }}>
              Selected ({selected.length})
            </div>
            {selected.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'center', padding: '20px 0' }}>
                Click universities to add them
              </div>
            )}
            {selected.map((u, i) => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                <span style={{ fontSize: 10, fontWeight: 800, color: '#7c3aed', background: 'rgba(124,58,237,0.1)', borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i+1}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-secondary)' }}>{countryFlag(u.country)} {u.country}</div>
                </div>
                <button onClick={() => toggle(u)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', fontSize: 14, flexShrink: 0 }}>×</button>
              </div>
            ))}

            <div style={{ marginTop: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Degree Type</label>
              <select className="crm-select" value={degreeType} onChange={e => setDegreeType(e.target.value)} style={{ width: '100%', marginTop: 4, fontSize: 12 }}>
                <option value="">Select…</option>
                {DEGREE_TYPES.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)' }}>Counsellor Notes</label>
              <textarea className="crm-input" value={notes} onChange={e => setNotes(e.target.value)}
                rows={4} placeholder="Add personalised notes for the student…"
                style={{ width: '100%', marginTop: 4, fontSize: 12, resize: 'none' }} />
            </div>

            <button onClick={handleGenerate} disabled={!selected.length || generating}
              className="btn-primary"
              style={{ width: '100%', marginTop: 14, padding: '11px 0', fontSize: 13, opacity: !selected.length ? 0.5 : 1 }}>
              {generating ? '⏳ Generating…' : '📄 Generate PDF'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main Universities Page ───────────────────────────────────────────────────
export default function UniversitiesPage() {
  const { currentUser, company, leads } = useCRM();
  const isAdmin = currentUser?.role === 'admin' || currentUser?.role === 'superadmin';

  // Custom universities from Firestore
  const [customUnis, setCustomUnis] = useState([]);
  const [loadingCustom, setLoadingCustom] = useState(true);

  useEffect(() => {
    if (!company?.id) return;
    const unsub = onSnapshot(
      collection(db, 'companies', company.id, 'universities'),
      snap => {
        setCustomUnis(snap.docs.map(d => ({ ...d.data(), id: d.id, _custom: true })));
        setLoadingCustom(false);
      },
      () => setLoadingCustom(false)
    );
    return unsub;
  }, [company?.id]);

  const allUniversities = useMemo(() => {
    const seed = SEED_UNIVERSITIES.map(u => ({ ...u, _seed: true }));
    return [...seed, ...customUnis];
  }, [customUnis]);

  // ── Filter state ───────────────────────────────────────────────────────
  // IMPORTANT: there is exactly ONE filter state object now. Every control —
  // the quick country pills, the search box, and every input inside the
  // "Advanced Filters" panel — writes directly to it via setF() and the grid
  // re-filters immediately. There used to be a separate draft/applied split
  // (panel inputs wrote to "draft", only an "Apply Filters" click pushed them
  // into "applied", which is what the grid actually used) which is exactly
  // why country/rank/fee filters appeared to do nothing: the quick pills
  // updated the live state instantly, but the panel's own Country/QS/US
  // News/Fee/Degree inputs silently only updated a staging copy until you
  // remembered to click Apply. Removing that split removes the whole bug class.
  const emptyFiltersState = {
    search: '', country: '', program: '', programCategory: '',
    degree: '', qsRankMax: '', usNewsRankMax: '', feeMax: '', intake: '',
  };
  const [filters, setFilters] = useState(emptyFiltersState);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('qsRank');
  const [viewMode, setViewMode] = useState('grid');
  // Undergrad / Graduate toggle — switches which level of US News program
  // rankings are shown on cards, the table, and the detail modal. QS subject
  // rankings don't split by level, so they're shown regardless of this toggle.
  const [degreeLevel, setDegreeLevel] = useState('grad');

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));
  const resetFilters = () => { setFilters(emptyFiltersState); setSearch(''); };

  const activeFilterCount = Object.values(filters).filter(v => v !== '').length + (search ? 1 : 0);

  // Selection state
  const [selectedUni, setSelectedUni] = useState(null);
  const [editModal, setEditModal] = useState(null);
  const [shortlistModal, setShortlistModal] = useState(null);

  // Multi-select for shortlisting: tick universities while browsing/filtering,
  // then send the current selection + active filters straight into the
  // ShortlistModal for a lead, instead of re-filtering from scratch there.
  const [bulkSelected, setBulkSelected] = useState([]);
  const toggleBulk = (u) => setBulkSelected(s =>
    s.find(x => x.id === u.id) ? s.filter(x => x.id !== u.id) : [...s, u]
  );
  const openShortlistFor = (lead) => { if (lead) setShortlistModal(lead); };
  const closeShortlistModal = () => { setShortlistModal(null); setBulkSelected([]); };

  const filtered = useMemo(() => {
    return allUniversities
      .filter(u => matchesFilters(u, filters, search))
      .sort((a, b) => {
        if (sortBy === 'qsRank') return (a.qsRank || 9999) - (b.qsRank || 9999);
        if (sortBy === 'usNewsRank') return (a.usNewsRank || 9999) - (b.usNewsRank || 9999);
        if (sortBy === 'tuition') return (a.tuitionMin || 0) - (b.tuitionMin || 0);
        if (sortBy === 'name') return a.name.localeCompare(b.name);
        return 0;
      });
  }, [allUniversities, filters, search, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveUni = async (form) => {
    try {
      if (form._firestoreId || form.id?.startsWith('custom-') || form._custom) {
        await updateDoc(doc(db, 'companies', company.id, 'universities', form._firestoreId || form.id), {
          ...form, updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'companies', company.id, 'universities'), {
          ...form, _custom: true, createdAt: serverTimestamp(),
        });
      }
    } catch (e) { console.error(e); }
    setEditModal(null);
  };

  const handleDeleteUni = async (u) => {
    if (!window.confirm(`Delete ${u.name}?`)) return;
    await deleteDoc(doc(db, 'companies', company.id, 'universities', u.id));
  };

  // Country stats
  const countryCounts = useMemo(() => {
    const counts = {};
    allUniversities.forEach(u => { counts[u.country] = (counts[u.country] || 0) + 1; });
    return counts;
  }, [allUniversities]);

  return (
    <div style={{ padding: '24px 28px', maxWidth: 1400, margin: '0 auto', height: '100%', overflowY: 'auto', boxSizing: 'border-box' }}>
      {/* Page Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>🎓 University Rankings</h1>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 4 }}>
            {allUniversities.length} universities across {Object.keys(countryCounts).length} countries · QS & US News Rankings 2026
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          {leads?.length > 0 && (
            <div style={{ position: 'relative' }}>
              <select className="crm-select" onChange={e => {
                const lead = leads.find(l => l.id === e.target.value);
                openShortlistFor(lead);
                e.target.value = '';
              }} style={{ fontSize: 12 }}>
                <option value="">📋 Create Shortlist for Lead{bulkSelected.length ? ` (${bulkSelected.length} selected)` : '…'}</option>
                {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
            </div>
          )}
          {isAdmin && (
            <button onClick={() => setEditModal({})} className="btn-primary" style={{ fontSize: 13, padding: '8px 16px' }}>
              + Add University
            </button>
          )}
        </div>
      </div>

      {/* Country Quick Tabs */}
      <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6, marginBottom: 18 }}>
        {[{ code: '', label: '🌏 All', flag: '' }, ...COUNTRIES].map(c => {
          const count = c.code ? (countryCounts[c.code] || 0) : allUniversities.length;
          const active = filters.country === c.code;
          return (
            <button key={c.code} onClick={() => setF('country', c.code)} style={{
              display: 'flex', alignItems: 'center', gap: 5,
              padding: '6px 14px', borderRadius: 20, fontSize: 12, cursor: 'pointer', flexShrink: 0,
              border: active ? '2px solid #7c3aed' : '1px solid var(--border)',
              background: active ? 'rgba(124,58,237,0.08)' : 'var(--bg-card)',
              color: active ? '#7c3aed' : 'var(--text-secondary)', fontWeight: active ? 700 : 500,
            }}>
              {c.flag || ''} {c.label?.replace(/^[^ ]+ /, '') || 'All'}
              <span style={{ fontSize: 10, background: active ? '#7c3aed' : 'var(--border)', color: active ? '#fff' : 'var(--text-secondary)', borderRadius: 10, padding: '1px 5px' }}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Search bar + Filter button row */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="crm-input" placeholder="🔍 Search by name, city or country…" value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 3, minWidth: 200 }} />

        {/* Filter toggle button */}
        <button onClick={() => setFiltersOpen(f => !f)} style={{
          display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', borderRadius: 10,
          border: filtersOpen ? '2px solid #7c3aed' : '1px solid var(--border)',
          background: filtersOpen ? 'rgba(124,58,237,0.08)' : 'var(--bg-card)',
          cursor: 'pointer', fontSize: 13, fontWeight: 600,
          color: filtersOpen ? '#7c3aed' : 'var(--text-secondary)',
        }}>
          <span>⚙ Filters</span>
          {activeFilterCount > 0 && (
            <span style={{ background: '#7c3aed', color: '#fff', borderRadius: 10, fontSize: 10, padding: '1px 6px', fontWeight: 700 }}>
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Sort */}
        <select className="crm-select" value={sortBy} onChange={e => setSortBy(e.target.value)} style={{ minWidth: 150 }}>
          <option value="qsRank">Sort: QS Rank</option>
          <option value="usNewsRank">Sort: US News</option>
          <option value="tuition">Sort: Lowest Fees</option>
          <option value="name">Sort: Name A–Z</option>
        </select>

        {/* Undergrad / Graduate toggle — controls which level of US News program
            rankings show on cards, table and the detail modal */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }} title="Switch program rankings between undergraduate and graduate level">
          {[{ v: 'ug', label: '🎓 Undergrad' }, { v: 'grad', label: '📚 Graduate' }].map(opt => (
            <button key={opt.v} onClick={() => setDegreeLevel(opt.v)} style={{
              padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 700,
              border: degreeLevel === opt.v ? '2px solid #d97706' : '1px solid var(--border)',
              background: degreeLevel === opt.v ? 'rgba(245,158,11,0.1)' : 'var(--bg-card)',
              color: degreeLevel === opt.v ? '#d97706' : 'var(--text-secondary)',
            }}>{opt.label}</button>
          ))}
        </div>

        {/* View toggle */}
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          {['grid', 'table'].map(v => (
            <button key={v} onClick={() => setViewMode(v)} style={{
              padding: '8px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
              border: viewMode === v ? '2px solid #7c3aed' : '1px solid var(--border)',
              background: viewMode === v ? 'rgba(124,58,237,0.08)' : 'var(--bg-card)',
              color: viewMode === v ? '#7c3aed' : 'var(--text-secondary)',
            }}>{v === 'grid' ? '⊞' : '☰'}</button>
          ))}
        </div>

        {activeFilterCount > 0 && (
          <button onClick={resetFilters} style={{ fontSize: 12, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', padding: '0 4px' }}>
            ✕ Clear all
          </button>
        )}
      </div>

      {/* Expanded Filter Panel */}
      {filtersOpen && (
        <div style={{
          background: 'var(--bg-card)', border: '1px solid var(--border)',
          borderRadius: 14, padding: '20px 24px', marginBottom: 16,
          boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
            🔬 Advanced Filters
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 14 }}>

            {/* Specific Program */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>Specific Program</label>
              <input className="crm-input" list="prog-datalist" placeholder="e.g. Computer Science, MBA…"
                value={filters.program} onChange={e => setF('program', e.target.value)}
                style={{ width: '100%' }} />
              <datalist id="prog-datalist">
                {(AVAILABLE_PROGRAMS || []).map(p => <option key={p} value={p} />)}
              </datalist>
            </div>

            {/* Program Category */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>Program Category</label>
              <select className="crm-select" value={filters.programCategory} onChange={e => setF('programCategory', e.target.value)} style={{ width: '100%' }}>
                <option value="">All Categories</option>
                {PROGRAM_CATEGORIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>

            {/* Degree Type */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>Degree Type</label>
              <select className="crm-select" value={filters.degree} onChange={e => setF('degree', e.target.value)} style={{ width: '100%' }}>
                <option value="">All Degrees</option>
                {DEGREE_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>

            {/* Country */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>Country</label>
              <select className="crm-select" value={filters.country} onChange={e => setF('country', e.target.value)} style={{ width: '100%' }}>
                <option value="">All Countries</option>
                {COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.label.replace(/^[^ ]+ /, '')}</option>)}
              </select>
            </div>

            {/* QS Rank Max */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>
                QS Top N <span style={{ color: '#94a3b8', fontWeight: 400 }}>(ranked only)</span>
              </label>
              <input type="number" className="crm-input" placeholder="e.g. 100 → shows QS #1–100"
                value={filters.qsRankMax} onChange={e => setF('qsRankMax', e.target.value)} style={{ width: '100%' }} />
            </div>

            {/* US News Rank Max */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>
                US News Top N <span style={{ color: '#94a3b8', fontWeight: 400 }}>(ranked only)</span>
              </label>
              <input type="number" className="crm-input" placeholder="e.g. 50 → shows US News #1–50"
                value={filters.usNewsRankMax} onChange={e => setF('usNewsRankMax', e.target.value)} style={{ width: '100%' }} />
            </div>

            {/* Max Tuition */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>
                Max Tuition / Year <span style={{ color: '#94a3b8', fontWeight: 400 }}>(local currency)</span>
              </label>
              <input type="number" className="crm-input" placeholder="e.g. 40000"
                value={filters.feeMax} onChange={e => setF('feeMax', e.target.value)} style={{ width: '100%' }} />
            </div>

            {/* Intake Month */}
            <div>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 5 }}>Intake Month</label>
              <select className="crm-select" value={filters.intake} onChange={e => setF('intake', e.target.value)} style={{ width: '100%' }}>
                <option value="">Any Intake</option>
                {['January','February','March','April','May','June','July','August','September','October','November','December'].map(m => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Every input above filters live — there is no separate "Apply" step.
              This row just confirms the live result count and lets you clear everything. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              <strong style={{ color: 'var(--text-primary)' }}>{filtered.length}</strong> universities match current filters
            </span>
            <button onClick={resetFilters} style={{
              padding: '10px 20px', borderRadius: 10, border: '1px solid var(--border)',
              background: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)', fontWeight: 600,
            }}>
              Reset All
            </button>
            <button onClick={() => setFiltersOpen(false)} className="btn-primary" style={{ padding: '10px 28px', fontSize: 13, fontWeight: 700, marginLeft: 'auto' }}>
              Done
            </button>
          </div>
        </div>
      )}

      {/* Active filter chips */}
      {activeFilterCount > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {search && <FilterChip label={`"${search}"`} onRemove={() => setSearch('')} />}
          {filters.country && <FilterChip label={countryLabel(filters.country)} onRemove={() => setF('country', '')} />}
          {filters.program && <FilterChip label={`Program: ${filters.program}`} onRemove={() => setF('program', '')} />}
          {filters.programCategory && <FilterChip label={filters.programCategory} onRemove={() => setF('programCategory', '')} />}
          {filters.degree && <FilterChip label={filters.degree} onRemove={() => setF('degree', '')} />}
          {filters.qsRankMax && <FilterChip label={`QS Top ${filters.qsRankMax}`} onRemove={() => setF('qsRankMax', '')} />}
          {filters.usNewsRankMax && <FilterChip label={`US News Top ${filters.usNewsRankMax}`} onRemove={() => setF('usNewsRankMax', '')} />}
          {filters.feeMax && <FilterChip label={`Fees ≤ $${Number(filters.feeMax).toLocaleString()}`} onRemove={() => setF('feeMax', '')} />}
          {filters.intake && <FilterChip label={`Intake: ${filters.intake}`} onRemove={() => setF('intake', '')} />}
        </div>
      )}

      {/* Results count */}
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14 }}>
        Showing <strong style={{ color: 'var(--text-primary)' }}>{filtered.length}</strong> of {allUniversities.length} universities
        {filters.country && (
          <span style={{ marginLeft: 8, color: '#7c3aed', fontWeight: 600 }}>
            · {COUNTRIES.find(c => c.code === filters.country)?.flag} {countryLabel(filters.country)}
          </span>
        )}
      </div>

      {/* Grid View */}
      {viewMode === 'grid' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map(u => (
            <UniversityCard
              key={u.id} u={u}
              selected={selectedUni?.id === u.id}
              onSelect={setSelectedUni}
              onEdit={isAdmin ? setEditModal : null}
              onDelete={isAdmin ? handleDeleteUni : null}
              isAdmin={isAdmin}
              inShortlist={!!bulkSelected.find(x => x.id === u.id)}
              onToggleShortlist={leads?.length > 0 ? toggleBulk : null}
              programCategory={filters.programCategory}
              degreeLevel={degreeLevel}
            />
          ))}
          {filtered.length === 0 && (
            <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px 0', color: 'var(--text-secondary)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>No universities match your filters</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>Try adjusting or clearing your filters</div>
              <button onClick={resetFilters} className="btn-primary" style={{ marginTop: 16, padding: '9px 22px', fontSize: 13 }}>Clear All Filters</button>
            </div>
          )}
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div style={{ overflowX: 'auto', background: 'var(--bg-card)', borderRadius: 14, border: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--border)' }}>
                {[
                  '#', 'University', 'Country', 'QS Rank', 'US News',
                  ...(filters.programCategory && PROGRAM_FIELD_MAP[filters.programCategory]
                    ? [`${PROGRAM_FIELD_MAP[filters.programCategory]} (${degreeLevel === 'ug' ? 'UG' : 'Grad'})`]
                    : []),
                  'Tuition / Year', 'Intakes', 'IELTS', 'Accept.', '',
                ].map(h => (
                  <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((u, i) => (
                <tr key={u.id} onClick={() => setSelectedUni(u)} style={{
                  borderBottom: '1px solid var(--border)', cursor: 'pointer',
                  background: selectedUni?.id === u.id ? 'rgba(124,58,237,0.05)' : 'transparent',
                  transition: 'background 0.1s',
                }}>
                  <td style={{ padding: '10px 14px', fontSize: 12, color: 'var(--text-secondary)', width: 36, textAlign: 'center' }}>{i + 1}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>{u.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>{u.city}</div>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 20 }}>{countryFlag(u.country)}</span>
                      <span style={{ fontSize: 11, color: 'var(--text-secondary)', fontWeight: 600 }}>
                        {COUNTRIES.find(c => c.code === u.country)?.label.replace(/^[^ ]+ /, '') || u.country}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    {u.qsRank ? <span style={{ fontWeight: 800, color: '#7c3aed' }}>#{u.qsRank}</span> : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                    {u.usNewsRank ? <span style={{ fontWeight: 800, color: '#0ea5e9' }}>#{u.usNewsRank}</span> : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  {filters.programCategory && PROGRAM_FIELD_MAP[filters.programCategory] && (
                    <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                      {getProgramRank(u, filters.programCategory, degreeLevel)
                        ? <span style={{ fontWeight: 800, color: '#d97706' }}>#{getProgramRank(u, filters.programCategory, degreeLevel)}</span>
                        : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                    </td>
                  )}
                  <td style={{ padding: '10px 14px', fontSize: 12, whiteSpace: 'nowrap' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{fmt(u.tuitionMin, u.currency)}</span>
                    {u.tuitionMax && <span style={{ color: 'var(--text-secondary)' }}> – {fmt(u.tuitionMax, u.currency)}</span>}
                    <span style={{ color: 'var(--text-secondary)', fontSize: 10 }}>/yr</span>
                  </td>
                  <td style={{ padding: '10px 14px', fontSize: 11, color: 'var(--text-secondary)' }}>{(u.intakes || []).join(', ') || '—'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12 }}>{u.ielts ? u.ielts + '+' : '—'}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'center', fontSize: 12 }}>
                    {u.acceptanceRate ? <span style={{ color: '#10b981', fontWeight: 700 }}>{u.acceptanceRate}%</span> : <span style={{ color: 'var(--text-secondary)' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px' }} onClick={e => e.stopPropagation()}>
                    {isAdmin && (
                      <button onClick={() => setEditModal(u)} style={{ background: 'rgba(124,58,237,0.1)', border: 'none', borderRadius: 6, padding: '3px 7px', cursor: 'pointer', fontSize: 11, color: '#7c3aed' }}>✏️</button>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={filters.programCategory && PROGRAM_FIELD_MAP[filters.programCategory] ? 11 : 10} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>No universities match your filters</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Selected university detail modal — a proper centered modal with backdrop,
          matching the same convention as UniversityModal/ShortlistModal elsewhere
          in this file (position:fixed inset:0 + dark backdrop + zIndex 9999+).
          This replaces the old position:fixed right:0 top:0 side panel, which sat
          underneath the sticky page Header's stacking context and visually
          collided/overlapped with it. Clicking the backdrop closes the modal. */}
      {selectedUni && (
        <div
          onClick={() => setSelectedUni(null)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: 20,
          }}
        >
        <div onClick={e => e.stopPropagation()} style={{
          height: 'calc(100vh - 40px)', width: 380, maxWidth: '100%',
          background: 'var(--bg-primary)', borderRadius: 16,
          boxShadow: '0 12px 48px rgba(0,0,0,0.3)', overflowY: 'auto', padding: 24,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 22 }}>{countryFlag(selectedUni.country)}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--text-secondary)', marginTop: 2 }}>
                {COUNTRIES.find(c => c.code === selectedUni.country)?.label.replace(/^[^ ]+ /, '') || selectedUni.country}
              </div>
            </div>
            <button onClick={() => setSelectedUni(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: 'var(--text-secondary)' }}>×</button>
          </div>
          <h2 style={{ fontSize: 16, fontWeight: 800, color: 'var(--text-primary)', marginBottom: 4 }}>{selectedUni.name}</h2>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 16 }}>{selectedUni.city}</div>

          {/* Rankings */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {selectedUni.qsRank && (
              <div style={{ flex: 1, background: 'rgba(124,58,237,0.07)', borderRadius: 10, padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#7c3aed' }}>#{selectedUni.qsRank}</div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>QS World 2026</div>
              </div>
            )}
            {selectedUni.usNewsRank && (
              <div style={{ flex: 1, background: 'rgba(14,165,233,0.07)', borderRadius: 10, padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#0ea5e9' }}>#{selectedUni.usNewsRank}</div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>US News 2026</div>
              </div>
            )}
            {selectedUni.acceptanceRate && (
              <div style={{ flex: 1, background: 'rgba(16,185,129,0.07)', borderRadius: 10, padding: '10px', textAlign: 'center' }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: '#10b981' }}>{selectedUni.acceptanceRate}%</div>
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', fontWeight: 600 }}>Acceptance</div>
              </div>
            )}
          </div>

          {/* Program Rankings — US News CS/Engineering/Business switch between
              undergrad and grad with the page-level toggle; Law/Medicine/Education
              only ever have a grad list (no UG list exists for these anywhere),
              so they're hidden while viewing Undergraduate. QS subject rankings
              don't split by level, so they always show as-is. */}
          {selectedUni.rankings && Object.values(selectedUni.rankings).some(v => v) && (
            <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 10 }}>
                PROGRAM RANKINGS — {degreeLevel === 'ug' ? 'UNDERGRADUATE' : 'GRADUATE'}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {[
                  ['CS (US News)', degreeLevel === 'ug' ? selectedUni.rankings.ugCS : selectedUni.rankings.gradCS],
                  ['Engineering (US News)', degreeLevel === 'ug' ? selectedUni.rankings.ugEngineering : selectedUni.rankings.gradEngineering],
                  ['Business (US News)', degreeLevel === 'ug' ? selectedUni.rankings.ugBusiness : selectedUni.rankings.gradBusiness],
                  ...(degreeLevel === 'grad' ? [
                    ['Law (US News)', selectedUni.rankings.gradLaw],
                    ['Medicine (US News)', selectedUni.rankings.gradMedicine],
                    ['Education (US News)', selectedUni.rankings.gradEducation],
                  ] : []),
                  ['QS Subject: CS', selectedUni.rankings.qsCS],
                  ['QS Subject: Engineering', selectedUni.rankings.qsEngineering],
                  ['QS Subject: Business', selectedUni.rankings.qsBusiness],
                ].filter(([, v]) => v).map(([label, val]) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11 }}>
                    <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
                    <span style={{ fontWeight: 700, color: '#7c3aed' }}>#{val}</span>
                  </div>
                ))}
              </div>
              {degreeLevel === 'ug' && (selectedUni.rankings.gradLaw || selectedUni.rankings.gradMedicine || selectedUni.rankings.gradEducation) && (
                <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 8, fontStyle: 'italic' }}>
                  Law, Medicine & Education have no undergraduate US News list (JD/MD/EdD are graduate-only credentials) — switch to Graduate to see those rankings.
                </div>
              )}
            </div>
          )}

          {/* Tuition */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>TUITION / YEAR</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)' }}>
              {fmt(selectedUni.tuitionMin, selectedUni.currency)} – {fmt(selectedUni.tuitionMax, selectedUni.currency)}
            </div>
          </div>

          {/* Requirements */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
            {selectedUni.ielts && <span style={{ fontSize: 11, background: 'rgba(14,165,233,0.08)', color: '#0ea5e9', borderRadius: 6, padding: '4px 10px', fontWeight: 600 }}>IELTS {selectedUni.ielts}+</span>}
            {selectedUni.toefl && <span style={{ fontSize: 11, background: 'rgba(14,165,233,0.08)', color: '#0ea5e9', borderRadius: 6, padding: '4px 10px', fontWeight: 600 }}>TOEFL {selectedUni.toefl}+</span>}
            {selectedUni.greRequired && <span style={{ fontSize: 11, background: 'rgba(245,158,11,0.08)', color: '#d97706', borderRadius: 6, padding: '4px 10px', fontWeight: 600 }}>GRE Required</span>}
          </div>

          {/* Intakes */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>INTAKES</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(selectedUni.intakes || []).map(m => (
                <span key={m} style={{ fontSize: 11, background: 'rgba(124,58,237,0.08)', color: '#7c3aed', borderRadius: 6, padding: '4px 10px', fontWeight: 600 }}>{m}</span>
              ))}
            </div>
          </div>

          {/* Programs */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>PROGRAMS OFFERED</div>
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {(selectedUni.programs || []).map(p => <Pill key={p} label={p} />)}
            </div>
          </div>

          {/* About */}
          {selectedUni.about && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>ABOUT</div>
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>{selectedUni.about}</p>
            </div>
          )}

          {/* Website */}
          {selectedUni.website && (
            <a href={selectedUni.website} target="_blank" rel="noopener noreferrer" style={{ display: 'block', fontSize: 12, color: '#7c3aed', fontWeight: 600, marginBottom: 14 }}>
              🔗 Visit Website
            </a>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 8 }}>
            {leads?.length > 0 && (
              <button onClick={() => openShortlistFor(leads[0])} className="btn-primary" style={{ flex: 1, fontSize: 12, padding: '9px 0' }}>
                📋 Add to Shortlist
              </button>
            )}
            {isAdmin && (
              <button onClick={() => setEditModal(selectedUni)} style={{
                flex: 1, fontSize: 12, padding: '9px 0', borderRadius: 10,
                border: '1px solid var(--border)', background: 'none', cursor: 'pointer',
                color: 'var(--text-secondary)', fontWeight: 600,
              }}>✏️ Edit</button>
            )}
          </div>
        </div>
        </div>
      )}

      {/* Floating bulk-selection bar — appears once universities are ticked for shortlisting */}
      {bulkSelected.length > 0 && !shortlistModal && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 14,
          boxShadow: '0 8px 30px rgba(0,0,0,0.18)', padding: '10px 16px',
          display: 'flex', alignItems: 'center', gap: 12, zIndex: 500,
        }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
            ✓ {bulkSelected.length} selected
          </span>
          {leads?.length > 0 ? (
            <select className="crm-select" defaultValue="" onChange={e => {
              const lead = leads.find(l => l.id === e.target.value);
              openShortlistFor(lead);
              e.target.value = '';
            }} style={{ fontSize: 12 }}>
              <option value="" disabled>📋 Create Shortlist for…</option>
              {leads.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          ) : (
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Add a lead to create a shortlist</span>
          )}
          <button onClick={() => setBulkSelected([])} style={{
            background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12,
          }}>Clear</button>
        </div>
      )}

      {/* Edit / Add Modal */}
      {editModal && (
        <UniversityModal uni={editModal?.id ? editModal : null} onClose={() => setEditModal(null)} onSave={handleSaveUni} />
      )}

      {/* Shortlist Modal — seeded with whatever the user already ticked/filtered
          on the main page, so filtering and shortlisting are one continuous flow */}
      {shortlistModal && (
        <ShortlistModal
          lead={shortlistModal}
          allUniversities={allUniversities}
          company={company}
          currentUser={currentUser}
          initialSelected={bulkSelected}
          initialFilters={filters}
          initialSearch={search}
          onClose={closeShortlistModal}
        />
      )}
    </div>
  );
}